/* eslint-disable max-len */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import { filter } from 'lodash/fp';
import _ from 'lodash';
import Container from 'components/container';
import Box from 'components/box';
import { SelectField } from 'components/inputs';
import Button from 'components/button';
import { Layout } from 'components/layouts';
import AdministrativeDivisionPicker from 'components/inputs/administrative-division-picker';
import { fetchAdministrativeDivisions } from 'store/administrative-divisions/administrative-divisions-actions';
import { fetchEnrollmentPeriodsByAdministrativeDivision } from 'store/enrollment/enrollment-actions';
import { fetchIncorrectMembers } from 'store/members/members-actions';

import { administrativeDivisionsByIdSelector, viewableAdminDivisionIdsSelector } from 'store/administrative-divisions/administrative-divisions-selectors';
import { enrollmentPeriodByAdministrativeDivisionOptionsSelector } from 'store/enrollment/enrollment-selectors';
import IncorrectMembersTable from 'components/member/incorrect-members-table';
import LoadingIndicator from 'components/loading-indicator';
import { Alert } from 'components/alerts';

const EMPTY_VALUE = '-1';
const PAGE_SIZE = 100;
const EMPTY_FILTER = {
  administrativeDivisionId: undefined,
  enrollmentPeriod: EMPTY_VALUE,
  membershipStatus: undefined,
  paying: undefined,
  beneficiaries: false, // Always false to show only household heads (SELF)
};

class IncorrectMembersView extends Component {
  static mapStateToProps = (state) => {
    const viewableAdminDivisionIds = viewableAdminDivisionIdsSelector(state);
    const filteredAdministrativeDivisions = filter(
      ad => viewableAdminDivisionIds.includes(ad.id),
    )(administrativeDivisionsByIdSelector(state));

    return {
      memberList: state.members.memberList,
      administrativeDivisions: filteredAdministrativeDivisions,
      isLoadingAdministrativeDivisions: state.administrativeDivisions.isLoadingAdministrativeDivisions,
      enrollmentPeriodByAdministrativeDivisionOptions: enrollmentPeriodByAdministrativeDivisionOptionsSelector(state),
      isLoadingMemberList: state.members.isLoadingMemberList,
      memberListError: state.members.memberListError,
    };
  }

  static mapDispatchToProps = dispatch => ({
    loadAdministrativeDivisions() {
      dispatch(fetchAdministrativeDivisions());
      dispatch({ type: 'MEMBER_LIST_CLEAR' });
    },
    loadEnrollmentPeriodsByAdministrativeDivision(administrativeDivisionId) {
      dispatch(fetchEnrollmentPeriodsByAdministrativeDivision(administrativeDivisionId));
    },
    clearEnrollmentPeriodsByAdministrativeDivision() {
      dispatch({ type: 'CLEAR_ENROLLMENT_PERIODS_BY_ADMINISTRATIVE_DIVISION' });
    },
    loadMembersList: filters => dispatch(fetchIncorrectMembers(filters)),
  })

  constructor(props) {
    super(props);
    this.state = { filters: EMPTY_FILTER };
  }

  componentDidMount() {
    const { loadAdministrativeDivisions } = this.props;
    loadAdministrativeDivisions();
    this.setDefaultAdministrativeDivision();
  }

  componentDidUpdate(prevProps) {
    const { administrativeDivisions } = this.props;
    // Set default when administrative divisions become available
    if (prevProps.administrativeDivisions.length === 0 && administrativeDivisions.length > 0) {
      this.setDefaultAdministrativeDivision();
    }
  }

  setDefaultAdministrativeDivision = () => {
    const { administrativeDivisions, loadEnrollmentPeriodsByAdministrativeDivision } = this.props;
    const { filters: { administrativeDivisionId } } = this.state;

    // Set default administrative division from current user
    let selectedAdminDivisionId = administrativeDivisionId;
    if (administrativeDivisions.length > 0 && !selectedAdminDivisionId) {
      const authUser = JSON.parse(localStorage.getItem('authUser'));
      if (authUser && authUser.administrativeDivisionId) {
        const selectedAdministrativeDivision = _.find(administrativeDivisions, { id: authUser.administrativeDivisionId });
        if (selectedAdministrativeDivision) {
          selectedAdminDivisionId = selectedAdministrativeDivision.id;
        }
      }
    }
    if (selectedAdminDivisionId && selectedAdminDivisionId !== administrativeDivisionId) {
      const { filters } = this.state;
      this.updateFilters({ ...filters, administrativeDivisionId: selectedAdminDivisionId });
      loadEnrollmentPeriodsByAdministrativeDivision(selectedAdminDivisionId);
    }
  }

  handleAdministrativeDivisionChange = (administrativeDivisionId) => {
    const { loadEnrollmentPeriodsByAdministrativeDivision } = this.props;
    if (administrativeDivisionId) {
      loadEnrollmentPeriodsByAdministrativeDivision(administrativeDivisionId);
    }

    const { filters } = this.state;
    this.updateFilters({ ...filters, administrativeDivisionId, enrollmentPeriod: EMPTY_VALUE });
  }

  updateFilters = (filters) => {
    this.setState({ filters });
  }

  handleSelectChange = (e) => {
    const { filters } = this.state;
    const value = e.target.value === EMPTY_VALUE ? undefined : e.target.value;
    this.updateFilters({ ...filters, [e.target.name]: value });
  }

  loadMembers = () => {
    const { loadMembersList } = this.props;
    const { filters } = this.state;
    // Build search filters for incorrect members endpoint
    const searchFilters = {
      administrative_division_id: filters.administrativeDivisionId,
      enrollment_period: filters.enrollmentPeriod === EMPTY_VALUE ? undefined : filters.enrollmentPeriod,
      membership_status: filters.membershipStatus === EMPTY_VALUE ? undefined : filters.membershipStatus,
      paying: filters.paying !== undefined && filters.paying !== EMPTY_VALUE ? filters.paying : undefined,
      page: 1,
      limit: PAGE_SIZE,
    };
    // Remove undefined values
    Object.keys(searchFilters).forEach(key => searchFilters[key] === undefined && delete searchFilters[key]);
    loadMembersList(searchFilters);
  }

  render() {
    const {
      administrativeDivisions,
      enrollmentPeriodByAdministrativeDivisionOptions,
      memberList,
      isLoadingMemberList,
      memberListError,
    } = this.props;
    const { filters: { administrativeDivisionId, enrollmentPeriod, membershipStatus, paying } } = this.state;
    const disabled = !administrativeDivisionId || !enrollmentPeriod || enrollmentPeriod === EMPTY_VALUE;

    const membershipStatusOptions = [
      { value: 'Active', name: 'Active Only' },
      { value: 'Inactive', name: 'Inactive Only' },
    ];

    const payingOptions = [
      { value: EMPTY_VALUE, name: 'All' },
      { value: true, name: 'Paying' },
      { value: false, name: 'Indigent' },
    ];

    return (
      <Layout pageTitle="Incorrect Members">
        <Container>
          <h1 className="tw-text-2xl tw-font-bold tw-my-4">Incorrect Members</h1>
          <AdministrativeDivisionPicker
            handleChange={this.handleAdministrativeDivisionChange}
            administrativeDivisions={administrativeDivisions}
            administrativeDivisionId={administrativeDivisionId}
          />
          <Grid container>
            <Grid item xs={3}>
              <Box paddingRight={3} paddingBottom={3}>
                <SelectField
                  label="Enrollment period *"
                  key="enrollmentPeriod"
                  name="enrollmentPeriod"
                  options={enrollmentPeriodByAdministrativeDivisionOptions}
                  value={enrollmentPeriod}
                  onChange={this.handleSelectChange}
                  disabled={!administrativeDivisionId}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box paddingRight={3} paddingBottom={3}>
                <SelectField
                  label="Membership Status"
                  key="membershipStatus"
                  name="membershipStatus"
                  options={membershipStatusOptions}
                  value={membershipStatus || EMPTY_VALUE}
                  onChange={this.handleSelectChange}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box paddingRight={3} paddingBottom={3}>
                <SelectField
                  label="Membership Type"
                  key="paying"
                  name="paying"
                  options={payingOptions}
                  value={paying !== undefined ? paying : EMPTY_VALUE}
                  onChange={this.handleSelectChange}
                />
              </Box>
            </Grid>
            <Grid item xs={3}>
              <Box paddingBottom={3} paddingTop={2}>
                <Button small primary onClick={this.loadMembers} disabled={disabled}>
                  Search
                </Button>
              </Box>
            </Grid>
          </Grid>
          {isLoadingMemberList && (
            <Box flex alignItems="center" flexDirection="column" paddingTop={4}>
              <LoadingIndicator noun="members" title="Fetching Members, Please wait ....." />
            </Box>
          )}
          {memberListError && (
            <Box paddingTop={4}>
              <Alert type="error">{memberListError}</Alert>
            </Box>
          )}
          {!isLoadingMemberList && !memberListError && memberList && memberList.members && memberList.members.length > 0 && (
            <Box paddingTop={4}>
              <IncorrectMembersTable 
                data={memberList} 
                onRefresh={this.loadMembers}
                filters={this.state.filters}
              />
            </Box>
          )}
        </Container>
      </Layout>
    );
  }
}

IncorrectMembersView.propTypes = {
  administrativeDivisions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  enrollmentPeriodByAdministrativeDivisionOptions: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  loadAdministrativeDivisions: PropTypes.func.isRequired,
  loadEnrollmentPeriodsByAdministrativeDivision: PropTypes.func.isRequired,
  clearEnrollmentPeriodsByAdministrativeDivision: PropTypes.func.isRequired,
  loadMembersList: PropTypes.func.isRequired,
  memberList: PropTypes.shape({
    members: PropTypes.array,
  }).isRequired,
  isLoadingMemberList: PropTypes.bool.isRequired,
  memberListError: PropTypes.string,
};

IncorrectMembersView.defaultProps = {
  memberListError: null,
};

export default connect(IncorrectMembersView.mapStateToProps, IncorrectMembersView.mapDispatchToProps)(IncorrectMembersView);
